import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'motion/react';

interface TransitionVideoProps {
  videoSrc?: string;
  className?: string;
}

export function TransitionVideo({ 
  videoSrc = "/transai.mp4",
  className = "w-full h-full object-cover sm:object-contain relative z-10"
}: TransitionVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    const tryPlay = () => {
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {});
      }
    };

    video.load();
    tryPlay();

    const onPlay = () => setIsPlaying(true);
    const onPlaying = () => setIsPlaying(true);
    const onCanPlay = () => tryPlay();
    const onLoadedData = () => tryPlay();
    const onLoadedMetadata = () => tryPlay();

    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('loadedmetadata', onLoadedMetadata);

    const retryInterval = setInterval(() => {
      if (video && video.paused) {
        tryPlay();
      }
    }, 600);

    const handleUserGesture = () => {
      tryPlay();
    };
    window.addEventListener('pointerdown', handleUserGesture, { passive: true });
    window.addEventListener('keydown', handleUserGesture, { passive: true });

    return () => {
      clearInterval(retryInterval);
      window.removeEventListener('pointerdown', handleUserGesture);
      window.removeEventListener('keydown', handleUserGesture);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [videoSrc]);

  return (
    <div className="w-full h-full relative overflow-hidden bg-[#05050a] flex items-center justify-center">
      {/* 1. Ambient Cosmic Neural Glow Backdrop */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div 
          className="absolute -top-1/4 -left-1/4 w-[150%] h-[150%] bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.25)_0%,rgba(168,85,247,0.15)_30%,rgba(5,5,10,0.95)_70%)]"
          animate={{
            scale: [1, 1.08, 1],
            rotate: [0, 5, 0, -5, 0],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Pulsing Central Quantum Core */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-72 sm:h-72 rounded-full bg-indigo-500/20 blur-[50px]"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.8, 0.4]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orbiting Quantum Rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            className="w-40 h-40 sm:w-64 sm:h-64 rounded-full border border-indigo-500/30 border-dashed"
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          />
          <motion.div 
            className="absolute w-52 h-52 sm:w-80 sm:h-80 rounded-full border border-amber-500/20 border-dotted"
            animate={{ rotate: -360 }}
            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </div>

      {/* 2. Transition MP4 Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className={className}
        style={{
          filter: 'contrast(1.1) brightness(1.05)',
          opacity: isPlaying ? 1 : 0.85,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        <source src={videoSrc} type="video/mp4" />
        <source src={`./${videoSrc.replace(/^\//, '')}`} type="video/mp4" />
      </video>

      {/* 3. Cinematic Ambient Vignette Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05050a] via-transparent to-transparent opacity-40 pointer-events-none z-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050a]/40 via-transparent to-[#05050a]/40 pointer-events-none z-20" />
    </div>
  );
}
